<template>
  <PrimaryModal @clickBackground="close()">
    <div
      class="modal-size max-w-screen-lg box-border px-12 py-4 m-4 overflow-y-scroll select-none text-gray-800 dark:text-dark-text"
      :class="{
        'custom-scrollbar': isLightMode(),
        'custom-scrollbar-dark': !isLightMode()
      }"
    >
      <div v-if="nested.selectedArenaId.value == ''">
        <div class="mt-4 mb-3 border-2 rounded bg-white dark:bg-dark-500 dark:border-dark-400">
          <div
            class="text-2xl font-bold border pl-3 py-2 flex bg-primary-500 text-white dark:bg-dark-400 dark:border-dark-400 dark:text-dark-text"
          >
            <h2 class="mt-1">{{ $t('message.arena') }}</h2>
            <div class="flex-grow" />
            <IconTextButton
              :border_radius="2"
              class="mx-2"
              :color_list="[
                'text-primary-500',
                'border-primary-500',
                'bg-white',
                'hover:border-primary-500',
                'hover:bg-gray-200',
                'active:border-primary-500',
                'active:bg-gray-300'
              ]"
              :font_weight="'700'"
              :icon="'open_in_new'"
              :iconSize="'text-xl'"
              :padding_bottom="2"
              :padding_left="12"
              :padding_right="12"
              :padding_top="2"
              :text="$t('message.altPage')"
              :textSize="'text-base'"
              @click="clickAltanativeMainPage()"
            />
          </div>
          <p class="p-3 pl-4">{{ $t('message.showEntryList') }}</p>
        </div>
        <div
          v-for="(entry, index) in arenaPresenter.arenaUserEntryList.value"
          :key="index"
          class="cursor-pointer border rounded p-4 w-full hover:shadow-lg transition-shadow duration-200 mb-1 hover:border-arena text-gray-900 dark:border-dark-400 dark:text-dark-text dark:bg-dark-500 dark:hover:border-arena-dark"
          @click="clickArena(entry)"
        >
          <div class="flex items-center">
            <div class="border rounded px-1 text-arena border-arena dark:text-arena-dark dark:border-arena-dark">
              {{ formatDate(entry.entryDate) }}
            </div>
            <div class="ml-3 text-xl text-gray-800 dark:text-dark-text">
              {{ entry.arenaId }}
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="arenaPresenter.arenaProblemElementList.value && nested.selectedArenaId.value != ''">
        <div class="mt-4 mb-3 border-2 rounded bg-white dark:bg-dark-500 dark:border-dark-400">
          <div
            class="text-2xl font-bold border pl-3 py-2 flex bg-primary-500 text-white dark:bg-dark-400 dark:border-dark-400 dark:text-dark-text"
          >
            <IconButton
              class="mx-2"
              :color_list="['bg-white', 'hover:bg-gray-200', 'active:bg-gray-300']"
              :icon="'arrow_back'"
              :iconColor="'text-primary-500 dark:text-dark-text'"
              :padding_left="7"
              :padding_right="7"
              @click="clickBack()"
            />
            <h2 class="mt-1 mx-2">{{ nested.selectedArenaId.value }}</h2>
            <div class="flex-grow" />
            <IconTextButton
              :border_radius="2"
              class="mx-2"
              :color_list="[
                'text-primary-500',
                'border-primary-500',
                'bg-white',
                'hover:border-primary-500',
                'hover:bg-gray-200',
                'active:border-primary-500',
                'active:bg-gray-300'
              ]"
              :font_weight="'700'"
              :icon="'open_in_new'"
              :iconSize="'text-xl'"
              :padding_bottom="2"
              :padding_left="12"
              :padding_right="12"
              :padding_top="2"
              :text="$t('message.altPage')"
              :textSize="'text-base'"
              @click="clickAltanativeArenaPage()"
            />
          </div>
        </div>
        <div v-if="arenaPresenter.isLoading.value" class="flex justify-center items-center" style="height: 120px">
          <LoadingPanel />
        </div>
        <div v-else>
          <div
            v-for="(element, index) in arenaPresenter.arenaProblemElementList.value"
            :key="index"
            class="hover-hand border rounded p-4 w-full hover:shadow-lg transition-shadow duration-200 mb-1 hover:border-arena text-gray-900 dark:border-dark-400 dark:text-dark-text dark:bg-dark-500 dark:hover:border-arena-dark"
            @click="arenaPresenter.setArenaProblem(element.problemId, element.arenaId, element.id, index)"
          >
            <div class="text-xl flex items-center">
              <span class="material-icons mr-4">
                <span v-if="arenaPresenter.arenaSolvedProblemList.value.has(element.id)" class="text-arena">
                  check_circle
                </span>
                <span v-else class="text-gray-500 dark:text-dark-text"> radio_button_unchecked </span>
              </span>
              <div
                class="border rounded px-1 w-8 text-center text-arena border-arena dark:text-arena-dark dark:border-arena-dark"
              >
                {{ element.id }}
              </div>
              <div class="ml-3 text-gray-800 dark:text-dark-text">
                {{ element.problemId }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PrimaryModal>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, SetupContext } from 'vue'

import { ArenaUserEntry } from '@/domain/entities/arena/arenaUserEntry'
import IconButton from '@/presentation/components/Button/IconButton.vue'
import IconTextButton from '@/presentation/components/Button/IconTextButton.vue'
import LoadingPanel from '@/presentation/components/Loading/LoadingPanel.vue'
import PrimaryModal from '@/presentation/components/Modal/PrimaryModal.vue'
import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import formatDate from '@/util/date/formatDate'

export default defineComponent({
  components: { PrimaryModal, LoadingPanel, IconTextButton, IconButton },
  setup(props, ctx: SetupContext) {
    const { user } = useAuthStatePresenter()
    const { isLightMode } = useConfigStatePresenter()
    const arenaPresenter = useArenaPresenter()
    const modalStatePresenter = useModalStatePresenter()
    arenaPresenter.getByUserIdArenaEntryList(user.value.id)

    const selectedArenaId = ref('')

    const clickArena = async (entry: ArenaUserEntry) => {
      selectedArenaId.value = entry.arenaId
      await arenaPresenter.getArenaDetailData(entry.arenaId, user.value.id)
    }

    const clickAltanativeMainPage = () => {
      window.open('https://onlinejudge.u-aizu.ac.jp/services/arena.html', '_blank', 'noopener')
    }

    const clickAltanativeArenaPage = () => {
      window.open('https://onlinejudge.u-aizu.ac.jp/services/room.html#' + selectedArenaId.value, '_blank', 'noopener')
    }

    const clickBack = () => {
      selectedArenaId.value = ''
    }

    const hotKeys = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        close()
      }
    }

    const close = () => {
      modalStatePresenter.setIsShowArenaModal(false)
    }

    onMounted(() => {
      document.addEventListener('keydown', hotKeys)
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', hotKeys)
    })

    return {
      clickArena,
      clickAltanativeMainPage,
      clickAltanativeArenaPage,
      clickBack,
      close,
      formatDate,
      isLightMode,
      arenaPresenter,
      nested: { selectedArenaId }
    }
  }
})
</script>

<style lang="postcss" scoped>
.modal-size {
  width: 80vw;
  height: 80vh;
}
</style>
